import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/api";
import { useAuth } from "@/auth";

const CountryCtx = createContext(null);

export function CountryProvider({ children }) {
  const { user } = useAuth();
  const [code, setCodeState] = useState(() => localStorage.getItem("rail_country") || "TR");
  const [list, setList] = useState(null);
  const [loadError, setLoadError] = useState(false);

  // Resilient loader — retries up to 3 times with backoff so a transient
  // network blip on production doesn't leave the dropdown permanently empty.
  const reload = async (attempt = 0) => {
    try {
      const r = await api.get("/countries");
      setList(r.data.rows);
      setLoadError(false);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) return; // unauthenticated — handled by auth flow
      if (attempt < 2) {
        setTimeout(() => reload(attempt + 1), 800 * (attempt + 1));
      } else {
        console.error("Failed to load /countries after retries:", err);
        setLoadError(true);
        setList([]); // empty list, but at least the UI knows we tried
      }
    }
  };

  // Fetch (or refetch) the country list whenever auth status changes.
  // The very first mount happens BEFORE login, so /countries returns 401;
  // re-running once `user` arrives populates the dropdown.
  useEffect(() => {
    if (user) reload();
  }, [user]);

  useEffect(() => {
    localStorage.setItem("rail_country", code);
  }, [code]);

  const setCode = (c) => setCodeState(c);
  const current = (list || []).find((c) => c.code === code) || { code, name_en: code, flag: "" };

  return (
    <CountryCtx.Provider value={{ code, setCode, list: list || [], current, reload, loadError }}>
      {children}
    </CountryCtx.Provider>
  );
}

export const useCountry = () => useContext(CountryCtx) || { code: "TR", setCode: () => {}, list: [], current: { code: "TR" }, loadError: false };
